/**
 * Vector Math Utilities for Zenith
 * Used for tangent-based sliding calculations
 */

/**
 * 2D Vector type
 */
export interface Vector2 {
  x: number;
  y: number;
}

/**
 * Get the length/magnitude of a vector
 */
export function length(v: Vector2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

/**
 * Normalize a vector (make it unit length)
 */
export function normalize(v: Vector2): Vector2 {
  const len = length(v);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/**
 * Get the tangent (perpendicular) to a normal vector
 * Rotates the normal 90 degrees counter-clockwise
 */
export function getTangent(normal: Vector2): Vector2 {
  return { x: -normal.y, y: normal.x };
}

/**
 * Calculate dot product of two vectors
 */
export function dot(a: Vector2, b: Vector2): number {
  return a.x * b.x + a.y * b.y;
}

/**
 * Scale a vector by a scalar
 */
export function scale(v: Vector2, scalar: number): Vector2 {
  return { x: v.x * scalar, y: v.y * scalar };
}

/**
 * Add two vectors
 */
export function add(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

/**
 * Subtract vector b from vector a
 */
export function subtract(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

/**
 * Clamp a value between min and max
 */
export function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation between two vectors
 */
export function lerpVector(a: Vector2, b: Vector2, t: number): Vector2 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

/**
 * Get distance between two points
 */
export function distanceBetween(a: Vector2, b: Vector2): number {
  return length(subtract(b, a));
}

/**
 * Project vector a onto vector b
 */
export function project(a: Vector2, b: Vector2): Vector2 {
  const bNorm = normalize(b);
  const dotProduct = dot(a, bNorm);
  return scale(bNorm, dotProduct);
}
